import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ThumbsUpBurst from "@/components/ThumbsUpBurst";
import axios from "axios";
import { playPaymentChime } from "@/lib/chime";
import { Banknote, ChevronRight, Loader2, Phone, ShieldCheck, Smartphone, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { blockPopups, unblockPopups } from "@/lib/launchPopups";
import useKeyboardSheetStyle from "@/hooks/useKeyboardSheetStyle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `GHS ${Number(n || 0).toFixed(2)}`;
const errMsg = (e, fb) => e?.response?.data?.detail || fb;

export const MOMO_NETWORKS = [
  { id: "mtn", label: "MTN", color: "#FFCC00", text: "#1a1a1a" },
  { id: "telecel", label: "Telecel", color: "#E60000", text: "#fff" },
  { id: "at", label: "AT", color: "#0057B8", text: "#fff" },
];

export function guessNetwork(phone) {
  const p = String(phone || "").replace(/\D/g, "").replace(/^233/, "0");
  const pre = p.slice(0, 3);
  if (["024", "025", "053", "054", "055", "059"].includes(pre)) return "mtn";
  if (["020", "050"].includes(pre)) return "telecel";
  if (["026", "027", "056", "057"].includes(pre)) return "at";
  return "mtn";
}

export function NetworkPicker({ value, onChange, testPrefix = "momo-network" }) {
  return (
    <div className="grid grid-cols-3 gap-2" role="radiogroup">
      {MOMO_NETWORKS.map((n) => (
        <button key={n.id} type="button" onClick={() => onChange(n.id)} data-testid={`${testPrefix}-${n.id}`}
          className={`h-11 rounded-xl text-[15px] font-semibold border transition-[background-color,border-color,transform] active:scale-[0.97] ${value === n.id ? "border-transparent shadow-sm" : "border-black/10 bg-white text-gray-700"}`}
          style={value === n.id ? { backgroundColor: n.color, color: n.text } : undefined}>
          {n.label}
        </button>
      ))}
    </div>
  );
}

// Customer pays a pro by cash or MoMo (number → SMS code → approve prompt on phone).
export default function HubtelPaySheet({ open, onOpenChange, target, token, userPhone, onPaid, onCashChosen, initialStep = "choose", paidExternally = false }) {
  const payee = target?.payeeNoun || "your pro";
  const [step, setStep] = useState("choose");
  const [phone, setPhone] = useState("");
  const [picked, setPicked] = useState(false);
  const [network, setNetwork] = useState("mtn");
  const [otp, setOtp] = useState("");
  const [tip, setTip] = useState(0);
  const [payment, setPayment] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Reset only when the sheet opens, never mid-payment.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      const p = String(userPhone || "").replace(/\D/g, "").replace(/^233/, "0");
      setPhone(p); setNetwork(guessNetwork(p)); setPicked(false); setTip(Number(target?.tip) || 0); setStep(initialStep); setOtp(""); setPayment(null); setError("");
    }
    wasOpen.current = open;
  }, [open, userPhone, initialStep]);

  const paidRef = useRef(onPaid);
  paidRef.current = onPaid;
  // The bill was settled elsewhere (pro confirmed cash, or MoMo landed): show the confirmation.
  useEffect(() => {
    if (!open || !paidExternally) return;
    if (step === "cash-done") setStep("cash-paid");
    else if (step === "waiting") setStep("paid");
  }, [open, paidExternally, step]);

  useEffect(() => {
    if (!open) return;
    blockPopups("hubtel-pay");
    return () => unblockPopups("hubtel-pay");
  }, [open]);

  useEffect(() => {
    if (step !== "waiting" || !payment?.id) return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/hubtel/pay/${payment.id}?token=${token}`);
        setPayment(data);
        if (data.status === "paid") { setStep("paid"); paidRef.current?.(data); window.dispatchEvent(new Event("yorm:booking-updated")); }
        if (data.status === "failed" || data.status === "expired") { setError(data.error || "Payment was not completed"); setStep("failed"); }
        if (data.status === "cancelled") setStep("cash-done");
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [step, payment?.id, token]);

  // Until it's paid, keep checking the bill: the pro / driver may confirm cash at any point.
  useEffect(() => {
    if (!open || !target?.id || target.type === "service_charge" || ["paid", "cash-paid", "waiting"].includes(step)) return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/hubtel/target-status?target_type=${target.type}&target_id=${target.id}&token=${token}`);
        if (data.paid) { setStep("cash-paid"); paidRef.current?.({ status: "paid", method: "cash" }); window.dispatchEvent(new Event("yorm:booking-updated")); }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [open, step, target?.id, target?.type, token]);

  // Confirmed payments close the sheet on their own.
  const closeRef = useRef(onOpenChange);
  closeRef.current = onOpenChange;
  useEffect(() => {
    if (step !== "paid" && step !== "cash-paid") return;
    playPaymentChime();
    const t = setTimeout(() => closeRef.current(false), 2800);
    return () => clearTimeout(t);
  }, [step]);

  const starting = useRef(false);
  const start = async () => {
    if (starting.current) return; // one tap = one code
    starting.current = true;
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/hubtel/pay/start?token=${token}`, { target_type: target.type, target_id: target.id, phone, network, tip: target.noTip ? 0 : tip, amount: target.amount });
      setPayment(data); setStep("otp");
    } catch (e) { setError(errMsg(e, "Could not start the payment")); }
    setBusy(false);
    starting.current = false;
  };

  const confirm = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/hubtel/pay/${payment.id}/confirm?token=${token}`, { otp });
      setPayment(data);
      if (data.status === "failed") { setError(data.error); setStep("failed"); } else setStep("waiting");
    } catch (e) {
      setError(errMsg(e, "Could not confirm the code"));
      setOtp("");
      if (e?.response?.status === 409 && /cash/i.test(e?.response?.data?.detail || "")) setStep("cash-done");
      if (e?.response?.status === 503 || /expired|start again/i.test(e?.response?.data?.detail || "")) setStep("failed");
    }
    setBusy(false);
  };

  const payCash = async () => {
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/hubtel/pay/cash?token=${token}`, { target_type: target.type, target_id: target.id });
      setStep("cash-done"); onCashChosen?.();
    } catch (e) { setError(errMsg(e, "Could not save your choice")); }
    setBusy(false);
  };

  const resend = async () => {
    try { await axios.post(`${API}/hubtel/pay/${payment.id}/resend?token=${token}`); setError(""); setOtp(""); setSentAt(Date.now()); toast.success("New code sent"); }
    catch (e) {
      setError(errMsg(e, "Could not resend the code"));
      if ([400, 429, 503].includes(e?.response?.status)) setStep("failed");
    }
  };

  // "Call me with the code" unlocks 60s after the code was (re)sent.
  const [sentAt, setSentAt] = useState(0);
  const [waitLeft, setWaitLeft] = useState(60);
  const [called, setCalled] = useState(false);
  useEffect(() => {
    if (step !== "otp") { setCalled(false); return; }
    setSentAt(Date.now());
  }, [step]);
  useEffect(() => {
    if (step !== "otp" || !sentAt) return;
    const tick = () => setWaitLeft(Math.max(0, 60 - Math.floor((Date.now() - sentAt) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [step, sentAt]);
  const callMe = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/hubtel/pay/${payment.id}/call?token=${token}`);
      setError(""); setOtp(""); setCalled(true); setSentAt(Date.now());
      toast.success("Calling you now");
    } catch (e) { setError(errMsg(e, "Could not call you")); }
    setBusy(false);
  };

  // Phones: the sheet stays put; only the code boxes (and Confirm) glide up to sit above the keyboard.
  const kbLift = useKeyboardSheetStyle(open && step === "otp");
  useEffect(() => {
    // Android: keep the layout viewport (and so the sheet) still while the keyboard is up.
    if (!(open && step === "otp")) return undefined;
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return undefined;
    const prev = meta.getAttribute("content") || "";
    if (!/interactive-widget/.test(prev)) meta.setAttribute("content", `${prev}, interactive-widget=resizes-visual`);
    return () => meta.setAttribute("content", prev);
  }, [open, step]);
  const codeRef = useRef(null);
  const [codeShift, setCodeShift] = useState(0);
  const lifted = codeShift > 0;
  useLayoutEffect(() => {
    const el = codeRef.current;
    if (!kbLift || !el) { setCodeShift(0); return; }
    const parentTop = el.offsetParent ? el.offsetParent.getBoundingClientRect().top : 0;
    const bottom = parentTop + el.offsetTop + el.offsetHeight; // ignores the transform
    const keyTop = window.innerHeight - kbLift;
    setCodeShift(Math.max(0, Math.round(bottom - (keyTop - 12))));
  }, [kbLift, lifted]);
  const onOtpChange = (v) => { setOtp(v); if (v.length === 6) document.activeElement?.blur?.(); };

  if (!target) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent keyboardManaged className="pay-glass max-w-md p-0 gap-0 overflow-hidden bg-[#F2F2F7]" data-testid="hubtel-pay-sheet">
        <div className="px-5 pt-6 pb-4 bg-white border-b border-black/5">
          <DialogTitle className="text-[13px] font-semibold uppercase tracking-wider text-gray-500">{step === "choose" ? "Pay" : step.startsWith("cash") ? "Pay cash" : "Pay with MoMo"}</DialogTitle>
          <DialogDescription className="sr-only">Pay securely with mobile money</DialogDescription>
          <p className="mt-1 text-[34px] font-bold tracking-[-0.02em] text-gray-900 tabular-nums" data-testid="hubtel-pay-amount">{fmt(payment?.amount ?? (Number(target.amount || 0) + (step === "details" ? tip : 0)))}</p>
          <p className="text-[15px] text-gray-600">to <span className="font-semibold text-gray-900">{target.proName || payee}</span>{target.label ? ` · ${target.label}` : ""}</p>
          {(payment?.tip || (step === "details" && tip > 0)) ? (
            <p className="text-[13px] text-gray-500 mt-0.5" data-testid="hubtel-pay-tip-line">Includes a {fmt(payment?.tip || tip)} tip — all of it goes to {payee}</p>
          ) : null}
        </div>

        <div className="p-5 space-y-4">
          {step === "choose" && (
            <div className="rounded-2xl bg-white divide-y divide-black/5 overflow-hidden" data-testid="pay-method-choice">
              <MethodRow icon={<Smartphone size={20} className="text-[#4F988E]" />} title="Pay with MoMo" sub="MTN, Telecel or AT — approve on your phone"
                onClick={() => setStep("details")} testId="pay-method-momo" />
              <MethodRow icon={<Banknote size={20} className="text-[#34C759]" />} title="Pay cash" sub={`Hand the money to ${payee}`}
                onClick={() => setStep("cash")} testId="pay-method-cash" />
            </div>
          )}

          {step === "cash" && (
            <>
              <div className="rounded-2xl bg-white p-5 text-center" data-testid="pay-cash-confirm">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#34C759]/10 flex items-center justify-center mb-3"><Banknote size={26} className="text-[#34C759]" /></div>
                <p className="text-[17px] font-semibold text-gray-900">Pay {fmt(target.amount)} in cash</p>
                <p className="text-[15px] text-gray-600 mt-1">Give it to {target.proName || payee} directly. They'll confirm in the app once they've received it.</p>
              </div>
              <Button className="w-full h-12 rounded-xl bg-[#4F988E] hover:bg-[#3d7a72] text-[17px] font-semibold" disabled={busy} onClick={payCash} data-testid="pay-cash-submit">
                {busy ? <Loader2 className="animate-spin" size={18} /> : "I'll pay cash"}
              </Button>
              <button type="button" onClick={() => setStep("choose")} className="w-full text-[15px] font-medium text-[#4F988E]" data-testid="pay-cash-back">Choose another way</button>
            </>
          )}

          {step === "cash-done" && (
            <State icon={<Banknote size={30} className="text-[#34C759]" />} title="Waiting for confirmation" testId="pay-cash-success"
              body={`Hand ${fmt(target.amount)} to ${target.proName || payee}. This closes once they confirm they got it.`}
              extra={<>
                <Loader2 className="animate-spin text-[#4F988E]" size={22} data-testid="pay-cash-waiting" />
                <button type="button" onClick={() => setStep("details")} className="text-[15px] font-medium text-[#4F988E]" data-testid="pay-cash-momo-instead">Pay with MoMo instead</button>
              </>} />
          )}

          {step === "cash-paid" && (
            <State hero={<ThumbsUpBurst testId="pay-thumbs-up" />} title="Cash payment confirmed" testId="pay-cash-confirmed"
              body={`${target.proName || payee} confirmed they received ${fmt(target.amount)}.`}
              extra={<Button className="w-full h-12 rounded-xl bg-[#4F988E] hover:bg-[#3d7a72]" onClick={() => onOpenChange(false)} data-testid="pay-cash-done">Done</Button>} />
          )}

          {step === "details" && (
            <>
              <div className="rounded-2xl bg-white p-4 space-y-3">
                <label className="text-[13px] font-medium text-gray-500" htmlFor="hubtel-pay-phone">Pay from this MoMo number</label>
                <Input id="hubtel-pay-phone" inputMode="tel" value={phone} maxLength={10} placeholder="024 123 4567"
                  onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setPhone(v); if (v.length >= 3 && !picked) setNetwork(guessNetwork(v)); }}
                  className="h-12 text-[17px] rounded-xl" data-testid="hubtel-pay-phone-input" />
                <NetworkPicker value={network} onChange={(n) => { setPicked(true); setNetwork(n); }} testPrefix="hubtel-pay-network" />
              </div>
              {!target.noTip && <TipPicker value={tip} onChange={setTip} payee={payee} />}
              <p className="flex items-start gap-2 text-[13px] text-gray-500 px-1">
                <ShieldCheck size={16} className="text-[#4F988E] shrink-0 mt-0.5" />
                We'll send a code by text and email so you can confirm {target?.type === "service_charge" ? "this payment to Yorm" : `you're paying the right ${target?.payeeNoun ? "business" : "pro"}`}.
              </p>
              <Button className="w-full h-12 rounded-xl bg-[#4F988E] hover:bg-[#3d7a72] text-[17px] font-semibold" disabled={busy || phone.length !== 10} onClick={start} data-testid="hubtel-pay-send-code">
                {busy ? <Loader2 className="animate-spin" size={18} /> : "Send code"}
              </Button>
            </>
          )}

          {step === "otp" && payment && (
            <>
              <div className="rounded-2xl bg-white divide-y divide-black/5" data-testid="hubtel-pay-recipient">
                <Row label="Paying" value={payment.artisan_name || target.proName} />
                {payment.artisan_momo_masked && <Row label={target?.payeeNoun ? `${target.payeeNoun}'s MoMo` : "Pro's MoMo"} value={payment.artisan_momo_masked} />}
                {payment.tip > 0 && <Row label={target?.rideshare ? "Fare" : "Bill"} value={fmt(payment.base_amount)} />}
                {payment.tip > 0 && <Row label="Tip" value={fmt(payment.tip)} />}
                {!target?.noTip && (
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-[13px] text-gray-500">{payment.tip > 0 ? "Want a different tip?" : "Forgot the tip?"}</span>
                    <button type="button" onClick={() => { setStep("details"); setOtp(""); setPayment(null); setError(""); }} className="text-[14px] font-medium text-[#4F988E]" data-testid="hubtel-pay-change-tip">{payment.tip > 0 ? "Change tip" : "Add a tip"}</button>
                  </div>
                )}
                <Row label={payment.tip > 0 ? "Total" : "Amount"} value={fmt(payment.amount)} />
                <Row label="From" value={`${payment.payer_phone_masked} · ${payment.network_label}`} />
              </div>
              <div ref={codeRef} data-lifted={codeShift > 0 ? "1" : undefined} className={`space-y-3 transition-transform duration-[220ms] ease-out ${codeShift > 0 ? "relative z-10 rounded-2xl bg-white solid-panel shadow-[0_0_0_12px_#fff,0_12px_40px_12px_rgba(0,0,0,0.18)]" : ""}`} style={codeShift > 0 ? { transform: `translateY(-${codeShift}px)` } : undefined} data-testid="hubtel-pay-code-panel">
                <div className="rounded-2xl bg-white p-4 flex flex-col items-center gap-3">
                  <p className="text-[15px] text-gray-700 text-center">Code sent. Enter the 6-digit code to confirm.</p>
                  <InputOTP maxLength={6} value={otp} onChange={onOtpChange} autoFocus data-testid="hubtel-pay-otp-input">
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg bg-[#F2F2F7]" />)}
                    </InputOTPGroup>
                  </InputOTP>
                  <button type="button" onClick={resend} className="text-[14px] font-medium text-[#4F988E]" data-testid="hubtel-pay-resend">Resend code</button>
                  {waitLeft > 0 ? (
                    <p className="text-[12px] text-gray-400" data-testid="hubtel-pay-call-wait">No SMS yet? We can call you with the code in {waitLeft}s</p>
                  ) : (
                    <button type="button" onClick={callMe} disabled={busy} className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#4F988E] disabled:opacity-50" data-testid="hubtel-pay-call-me">
                      <Phone size={14} /> {called ? "Call me again" : "Call me with the code"}
                    </button>
                  )}
                </div>
                <Button className="w-full h-12 rounded-xl bg-[#4F988E] hover:bg-[#3d7a72] text-[17px] font-semibold" disabled={busy || otp.length !== 6} onClick={confirm} data-testid="hubtel-pay-confirm">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : `Confirm & pay ${fmt(payment.amount)}`}
                </Button>
              </div>
            </>
          )}

          {step === "waiting" && (
            <State icon={<Smartphone size={30} className="text-[#4F988E]" />} title="Approve on your phone" testId="hubtel-pay-waiting"
              body={`Check ${payment?.payer_phone_masked || "your phone"} for the ${payment?.network_label || "MoMo"} prompt and enter your PIN. This screen updates on its own.`}
              extra={<Loader2 className="animate-spin text-[#4F988E]" size={22} />} />
          )}

          {step === "paid" && (
            <State hero={<ThumbsUpBurst testId="pay-thumbs-up" />} title="Payment sent" testId="hubtel-pay-success"
              body={`${fmt(payment?.amount)} paid to ${payment?.artisan_name || target.proName}. We've let them know.`}
              extra={<Button className="w-full h-12 rounded-xl bg-[#4F988E] hover:bg-[#3d7a72]" onClick={() => onOpenChange(false)} data-testid="hubtel-pay-done">Done</Button>} />
          )}

          {step === "failed" && (
            <State icon={<XCircle size={34} className="text-[#FF3B30]" />} title="Payment not completed" testId="hubtel-pay-failed"
              body={error || "Nothing was charged. You can try again."}
              extra={<Button variant="outline" className="w-full h-12 rounded-xl" onClick={() => { setStep("details"); setError(""); setOtp(""); setPayment(null); setTip(0); }} data-testid="hubtel-pay-retry">Try again</Button>} />
          )}

          {error && ["choose", "cash", "details", "otp"].includes(step) && (
            <p className="text-[14px] text-[#FF3B30] text-center" data-testid="hubtel-pay-error">{error}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TIP_OPTIONS = [0, 2, 5, 10];

function TipPicker({ value, onChange, payee = "your pro" }) {
  const [custom, setCustom] = useState(false);
  return (
    <div className="rounded-2xl bg-white p-4 space-y-3" data-testid="hubtel-pay-tip">
      <p className="text-[13px] font-medium text-gray-500">Add a tip? <span className="text-gray-400">100% goes to {payee}</span></p>
      <div className="grid grid-cols-5 gap-2">
        {TIP_OPTIONS.map((t) => (
          <button key={t} type="button" onClick={() => { setCustom(false); onChange(t); }} data-testid={`hubtel-pay-tip-${t}`}
            className={`h-10 rounded-xl text-[14px] font-semibold border transition-[background-color,border-color] ${!custom && value === t ? "bg-[#4F988E] border-[#4F988E] text-white" : "bg-white border-black/10 text-gray-700"}`}>
            {t === 0 ? "None" : `₵${t}`}
          </button>
        ))}
        <button type="button" onClick={() => { setCustom(true); onChange(0); }} data-testid="hubtel-pay-tip-custom"
          className={`h-10 rounded-xl text-[14px] font-semibold border ${custom ? "bg-[#4F988E] border-[#4F988E] text-white" : "bg-white border-black/10 text-gray-700"}`}>Other</button>
      </div>
      {custom && (
        <Input inputMode="decimal" placeholder="Tip amount (GHS)" autoFocus className="h-11 rounded-xl text-[16px]" data-testid="hubtel-pay-tip-input"
          onChange={(e) => { const v = Math.min(1000, Math.max(0, parseFloat(e.target.value.replace(/[^\d.]/g, "")) || 0)); onChange(Math.round(v * 100) / 100); }} />
      )}
    </div>
  );
}

function MethodRow({ icon, title, sub, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="w-full flex items-center gap-3 px-4 py-4 text-left active:bg-black/5 transition-colors">
      <span className="w-10 h-10 rounded-xl bg-[#F2F2F7] flex items-center justify-center shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-[17px] font-semibold text-gray-900">{title}</span>
        <span className="block text-[13px] text-gray-500">{sub}</span>
      </span>
      <ChevronRight size={18} className="text-gray-300" />
    </button>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 text-[15px]">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}

function State({ icon, title, body, extra, testId, hero }) {
  return (
    <div className="rounded-2xl bg-white p-6 flex flex-col items-center text-center gap-3" data-testid={testId}>
      {hero ? <div className="py-3">{hero}</div> : <div className="w-16 h-16 rounded-full bg-[#F2F2F7] flex items-center justify-center">{icon}</div>}
      <p className="text-[20px] font-semibold text-gray-900">{title}</p>
      <p className="text-[15px] text-gray-600">{body}</p>
      {extra}
    </div>
  );
}
